import { useQuery } from '@tanstack/react-query';
import {
  getAllDepartments,
  getDepartmentsPaged,
  DepartmentListDto,
  DepartmentListParams,
  DepartmentListResult,
} from '../services/departmentService';
import { configurationKeys } from '../queryKeys';

/**
 * Fetches the department list for a tenant via React Query.
 * Mirrors the Tickets module's list-query hooks (see useTickets.ts).
 */
export function useDepartments(tenantId: string) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    DepartmentListDto[],
    Error
  >({
    queryKey: configurationKeys.departments.list(tenantId),
    queryFn: () => getAllDepartments(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });

  return {
    departments: data ?? [],
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetch,
  };
}

/**
 * Server-side paginated/searchable variant, used only by the Departments
 * Configuration screen's table. `useDepartments` above is left untouched — it
 * still serves the parent-department dropdown (and other screens) unpaginated.
 */
export function useDepartmentsList(params: DepartmentListParams) {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    DepartmentListResult,
    Error
  >({
    queryKey: configurationKeys.departments.paged(params),
    queryFn: () => getDepartmentsPaged(params),
    enabled: Boolean(params.tenantId),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });

  return {
    departments: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
    error: error ?? null,
    refetch,
  };
}
